/**
 * purchase-orders.service.ts
 *
 * Typed API call wrappers for the Purchase Orders domain.
 */
import { apiClient } from './apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export type POStatus = 'draft' | 'submitted' | 'partially_received' | 'received' | 'cancelled';

export interface PurchaseOrderItem {
  poItemId: number;
  purchaseOrderId: number;
  productId: number;
  productName: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  unitCost: number;
}

export interface PurchaseOrder {
  purchaseOrderId: number;
  accountId: number;
  vendorId: number;
  vendorName?: string;
  status: POStatus;
  orderNumber: string | null;
  orderDate: string | null;
  expectedDate: string | null;
  receivedDate: string | null;
  notes: string | null;
  totalCost: number | null;
  createdBy: number | null;
  items: PurchaseOrderItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePOPayload {
  vendorId: number;
  orderNumber?: string;
  orderDate?: string;
  expectedDate?: string;
  notes?: string;
  items: Array<{ productId: number; quantityOrdered: number; unitCost: number }>;
}

export interface UpdatePOPayload {
  orderNumber?: string;
  orderDate?: string;
  expectedDate?: string;
  notes?: string;
}

export interface ReceiveItemsPayload {
  items: Array<{ poItemId: number; quantityReceived: number }>;
}

export interface POListParams {
  status?: POStatus;
  vendorId?: number;
  search?: string;
  page?: number;
  perPage?: number;
  export?: boolean;
}

export interface PaginatedPOs {
  data: PurchaseOrder[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

/**
 * No `partially_received` count: that status (an order part-way through
 * receiving) has no tile of its own and counts only toward `total`.
 */
export interface PurchaseOrderStats {
  total: number;
  draft: number;
  submitted: number;
  received: number;
  cancelled: number;
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

export async function listPurchaseOrders(params?: POListParams): Promise<PaginatedPOs> {
  const res = await apiClient.get<PaginatedPOs>('/purchase-orders', { params });
  return res.data;
}

export async function getPurchaseOrderStats(): Promise<PurchaseOrderStats> {
  const res = await apiClient.get<{ data: PurchaseOrderStats }>('/purchase-orders/stats');
  return res.data.data;
}

/** Reserved for Phase 2 (PO detail view). Backend endpoint exists today. */
export async function getPurchaseOrder(poId: number): Promise<PurchaseOrder> {
  const res = await apiClient.get<{ data: PurchaseOrder }>(`/purchase-orders/${poId}`);
  return res.data.data;
}

export async function createPurchaseOrder(payload: CreatePOPayload): Promise<PurchaseOrder> {
  const res = await apiClient.post<{ data: PurchaseOrder }>('/purchase-orders', payload);
  return res.data.data;
}

/** Reserved for Phase 2 (PO edit flow). Backend endpoint exists today. */
export async function updatePurchaseOrder(poId: number, payload: UpdatePOPayload): Promise<PurchaseOrder> {
  const res = await apiClient.put<{ data: PurchaseOrder }>(`/purchase-orders/${poId}`, payload);
  return res.data.data;
}

export async function cancelPurchaseOrder(poId: number): Promise<void> {
  await apiClient.delete(`/purchase-orders/${poId}`);
}

export async function submitPurchaseOrder(poId: number): Promise<PurchaseOrder> {
  const res = await apiClient.post<{ data: PurchaseOrder }>(`/purchase-orders/${poId}/submit`);
  return res.data.data;
}

export async function receivePurchaseOrder(poId: number, payload: ReceiveItemsPayload): Promise<PurchaseOrder> {
  const res = await apiClient.post<{ data: PurchaseOrder }>(`/purchase-orders/${poId}/receive`, payload);
  return res.data.data;
}
